import { EmptyPinnedSlot } from "@/components/chat/chat-empty-pinned-slot";
import { PromptSuggestionCard } from "@/components/chat/chat-prompts-suggestions-card";
import DialogSelectPrompt from "@/components/prompt/dialog-select-prompt";
import { addPromptToHistory } from "@/components/prompt/prompts-popover";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { MAX_PINNED_PROMPTS } from "@/enums/pinned-prompts";
import { usePinnedPrompts } from "@/hooks/usePromptsPinned";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildPromptSnapshot,
  generatePromptsWithFavorites,
  generateRandomPrompts,
  getPinnedPromptsInCatalogueOrder,
  getPromptContent,
  getPromptId,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { ConversationMessagePromptInfo, PromptDTO } from "@shaarp/common";
import { Pin, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type ChatPromptsSuggestionsProps = {
  /** The tactic's text to send, and the tactic itself for the message (#681). */
  onPromptSelect: (
    content: string,
    prompt: ConversationMessagePromptInfo,
  ) => void;
  disabled?: boolean;
  /**
   * How much of the block is offered. "full" (the company chat) keeps the
   * bottom row, which swaps the pins for random tactics. "compact" (the empty
   * chat screen) drops it: the pinned tactics are the only shortcut that screen
   * offers, so a row that draws them away has nothing to hand back to.
   */
  variant?: "full" | "compact";
};

/**
 * Placeholder cards, shaped like the tactic cards they stand in for. Without
 * them the block opens on its empty slots, telling a user who has pinned
 * tactics that they have none until the request lands.
 */
function PromptSuggestionSkeletons({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={`skeleton-${index}`}
          aria-hidden
          className="flex items-center gap-2 rounded-xl border border-border/50 bg-background/50 p-3"
        >
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </>
  );
}

// Animation keyframes defined as inline styles for reliable animation
const ANIMATION_KEYFRAMES = `
@keyframes promptFadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes promptFadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
`;

// Inject animation styles once
if (typeof document !== "undefined") {
  const styleId = "prompt-suggestion-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = ANIMATION_KEYFRAMES;
    document.head.appendChild(style);
  }
}

export function ChatPromptsSuggestions({
  onPromptSelect,
  disabled = false,
  variant = "full",
}: ChatPromptsSuggestionsProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const lang = getSupportedLang(i18n.language);
  const toastError = useToastError();
  const { pinnedIds, customPrompts, refreshPinned, togglePinned, isLoading } =
    usePinnedPrompts({
      fetchCustomPrompts: true,
      listenForChanges: true,
    });

  const [displayedPrompts, setDisplayedPrompts] = useState<PromptDTO[]>([]);
  const [animationKey, setAnimationKey] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showingPinned, setShowingPinned] = useState(true);
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  // Visual state for rendering - only changes after fade out completes
  const [renderingPinned, setRenderingPinned] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Calculate actual pinned prompts that exist in customPrompts
  const pinnedOnlyPrompts = renderingPinned
    ? getPinnedPromptsInCatalogueOrder(customPrompts, pinnedIds)
    : displayedPrompts;

  // Use actual displayed prompts count for empty slots calculation
  const emptySlots = renderingPinned
    ? MAX_PINNED_PROMPTS.chat - pinnedOnlyPrompts.length
    : 0;

  useEffect(() => {
    // Only run on initial mount to set up pinned prompts
    if (!isInitialized && customPrompts.length > 0) {
      const prompts = generatePromptsWithFavorites(pinnedIds, customPrompts, 3);
      setDisplayedPrompts(prompts);
      setShowingPinned(true);
      setRenderingPinned(true);
      setIsInitialized(true);
    }
  }, [customPrompts, pinnedIds, isInitialized]);

  useEffect(() => {
    // Don't update displayedPrompts during transition to avoid interrupting animations
    if (showingPinned && !isTransitioning) {
      const prompts = generatePromptsWithFavorites(pinnedIds, customPrompts, 3);
      setDisplayedPrompts(prompts);
    }
  }, [pinnedIds, customPrompts, showingPinned, isTransitioning]);

  const handlePromptClick = useCallback(
    (prompt: PromptDTO) => {
      const content = getPromptContent(prompt, lang);
      addPromptToHistory(prompt, undefined, lang);
      onPromptSelect(content, buildPromptSnapshot(prompt, "", lang));
    },
    [lang, onPromptSelect],
  );

  const transitionToNewPrompts = useCallback(
    (getNewPrompts: () => PromptDTO[], newShowingPinned: boolean) => {
      setIsTransitioning(true);
      // Don't change renderingPinned yet - keep the old view for fade out

      setTimeout(() => {
        setDisplayedPrompts(getNewPrompts());
        setAnimationKey((prev) => prev + 1);
        setShowingPinned(newShowingPinned);
        setRenderingPinned(newShowingPinned); // Now change the visual state
        setIsTransitioning(false);
      }, 200);
    },
    [],
  );

  const handleRefresh = useCallback(() => {
    transitionToNewPrompts(
      () => generateRandomPrompts(customPrompts, 3),
      false,
    );
  }, [customPrompts, transitionToNewPrompts]);

  const handleShowPinned = useCallback(() => {
    refreshPinned();
    transitionToNewPrompts(
      () => generatePromptsWithFavorites(pinnedIds, customPrompts, 3),
      true,
    );
  }, [customPrompts, pinnedIds, refreshPinned, transitionToNewPrompts]);

  const handleAddPinnedSlot = useCallback(() => {
    setShowSelectDialog(true);
  }, []);

  const handleSelectPromptToPin = useCallback(
    (prompt: PromptDTO) => {
      const promptId = getPromptId(prompt);

      // Check if already pinned
      if (pinnedIds.has(promptId)) {
        toast({
          title: t("prompt.pinned.alreadyPinned.title"),
          description: t("prompt.pinned.alreadyPinned.description"),
        });
        return;
      }

      togglePinned(prompt).catch(() => toastError());
      setShowSelectDialog(false);
    },
    [pinnedIds, togglePinned, toast, toastError, t],
  );

  return (
    <div className="w-full mb-5">
      <div
        className="grid grid-cols-2 gap-2 md:grid-cols-3"
        aria-busy={isLoading}
      >
        {isLoading ? (
          <PromptSuggestionSkeletons count={MAX_PINNED_PROMPTS.chat} />
        ) : renderingPinned ? (
          <>
            {pinnedOnlyPrompts.map((prompt, index) => (
              <PromptSuggestionCard
                key={`${animationKey}-pinned-${index}`}
                prompt={prompt}
                index={index}
                isPinned={true}
                isTransitioning={isTransitioning}
                disabled={disabled}
                onClick={handlePromptClick}
                onUnpin={togglePinned}
              />
            ))}
            {Array.from({ length: emptySlots }).map((_, index) => (
              <EmptyPinnedSlot
                key={`${animationKey}-empty-${index}`}
                index={pinnedOnlyPrompts.length + index}
                isTransitioning={isTransitioning}
                disabled={disabled}
                onClick={handleAddPinnedSlot}
              />
            ))}
          </>
        ) : (
          <>
            {displayedPrompts.map((prompt, index) => (
              <PromptSuggestionCard
                key={`${animationKey}-${index}`}
                prompt={prompt}
                index={index}
                isPinned={pinnedIds.has(getPromptId(prompt))}
                isTransitioning={isTransitioning}
                disabled={disabled}
                onClick={handlePromptClick}
              />
            ))}
          </>
        )}
      </div>

      {/* Only the full block can leave the pins: with this row gone nothing
          flips `showingPinned`, so "compact" renders the real pins and their
          empty slots and never a random draw. */}
      {variant === "full" && (
        <div className="flex flex-row items-center justify-center gap-2 mt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleShowPinned}
            disabled={isTransitioning || disabled || isLoading || showingPinned}
            className={cn(
              "gap-2",
              showingPinned
                ? "text-muted-foreground/50 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400",
            )}
          >
            <Pin className="fill-current size-4" />
            {t("prompt.pinned.button")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isTransitioning || disabled || isLoading}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <RefreshCwIcon className="size-4" />
            {t("prompt.suggestions.others")}
          </Button>
        </div>
      )}

      {showSelectDialog && (
        <DialogSelectPrompt
          context="chat"
          onSelect={handleSelectPromptToPin}
          onClose={() => setShowSelectDialog(false)}
        />
      )}
    </div>
  );
}
